import posthog from 'posthog-js'
import { CORRECTIONS, HISTORY, QUIZ, SETTING, STORIES, STORY, STORY_FINISHED, WORDS } from '../routes/routeConstant'

// Smart link that sends phones to the right store (and desktops to a page listing both).
// The QR code in public/images/app-promo/onelink-qr.svg encodes this same URL.
export const APP_ONELINK_URL = 'https://onelink.to/ja4vkp'

// "Default Google Play rating" from the Play Console (Ratings > Overview), 2026-09-29. Update by
// hand when it moves. Play localises ratings, so a store page can show a different figure per country.
export const PLAY_STORE_RATING = 4.9

export const DESKTOP_PROMO_SEEN_KEY = 'app_promo_desktop_seen'
export const ANDROID_BANNER_DISMISSED_KEY = 'app_promo_android_dismissed'

export type MobilePlatform = 'ios' | 'android'
export type AppPromoSurface = 'desktop_toast' | 'desktop_modal' | 'android_banner'

interface DeviceInfo {
  userAgent: string
  maxTouchPoints: number
}

/** iPadOS 13+ Safari reports a Mac user agent, so a "Mac" with a touch screen is an iPad. */
export const getMobilePlatform = (device?: DeviceInfo): MobilePlatform | null => {
  const userAgent = device?.userAgent ?? (typeof navigator !== 'undefined' ? navigator.userAgent : '')
  const maxTouchPoints = device?.maxTouchPoints ?? (typeof navigator !== 'undefined' ? navigator.maxTouchPoints : 0)
  if (/android/i.test(userAgent)) return 'android'
  if (/iphone|ipad|ipod/i.test(userAgent)) return 'ios'
  if (/macintosh/i.test(userAgent) && maxTouchPoints > 1) return 'ios'
  return null
}

/** A real desktop: not a phone or tablet, and driven by a mouse or trackpad. */
export const isDesktopBrowser = () => {
  if (typeof window === 'undefined' || getMobilePlatform()) return false
  return window.matchMedia?.('(hover: hover) and (pointer: fine)').matches ?? false
}

// Only the pages a signed-in learner lives on. Signup, checkout, claim links, meetings and the
// teacher dashboard are all excluded so the promo never competes with a conversion step.
// The inline script in public/index.html that gates Safari's Smart App Banner mirrors this list:
// keep the two in sync.
const PROMO_EXACT_PATHS = [STORIES, HISTORY, WORDS, CORRECTIONS, SETTING, STORY_FINISHED]
const PROMO_PATH_PREFIXES = [`${STORY}/`, `${QUIZ}/`]

export const isAppPromoPath = (pathname: string) =>
  PROMO_EXACT_PATHS.includes(pathname) || PROMO_PATH_PREFIXES.some((prefix) => pathname.startsWith(prefix))

export const readFlag = (key: string) => {
  try {
    return localStorage.getItem(key) === 'true'
  } catch {
    return false
  }
}

export const writeFlag = (key: string) => {
  try {
    localStorage.setItem(key, 'true')
  } catch {
    /* storage unavailable: the promo simply shows again next visit */
  }
}

export const trackAppPromo = (event: string, surface: AppPromoSurface) => {
  try {
    posthog.capture(event, { surface })
  } catch {
    /* analytics must never break the page */
  }
}
